import { Injectable } from "@angular/core";
import posthog from 'posthog-js';
import { environment } from "src/environments/environment";
import { ANALYTICS_BLOCKED_KEYS, AnalyticsEventProperties, AnalyticsPersonProperties } from "./analytics.types";
import { Capacitor } from "@capacitor/core";
import { v4 as uuidv4 } from 'uuid';
import { AnalyticsEvents } from "./analytics.events";

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
    private initialized = false;
    private sessionId: string | null = null;
    private sessionStartTracked = false;
    private currentUserId: string | null = null;

    init(): void {
        if (this.initialized) return;
        if (!environment.analyticsEnabled || !environment.posthogKey?.trim()) return;

        try {
            posthog.init(environment.posthogKey, {
                api_host: environment.posthogHost || 'https://us.i.posthog.com',
                autocapture: false,
                capture_pageview: false,
                capture_pageleave: false,
                disable_session_recording: true,
            });

            this.initialized = true;
            this.sessionId = uuidv4();
            console.log('Analytics initialized with session ID:', this.sessionId);
        } catch (error) {
            console.warn('[Analytics] init failed', error);
        }
    }

    trackEvent(eventName: string, payload: AnalyticsEventProperties = {}): void {
        if (!this.initialized || !eventName) return;

        try {
            posthog.capture(eventName, {
                session_id: this.sessionId,
                platform: Capacitor.getPlatform(),
                user_id: this.currentUserId || undefined,
                ...this.sanitize(payload),
            });

            console.log('[Analytics] tracked event:', eventName, payload);
        } catch (error) {
            console.warn('[Analytics] trackEvent failed', error);
        }
    }

    /** identify + user_logged_in, at most once until reset() */
    trackUserLoggedIn(userId: string, loginMethod: string, personProps: AnalyticsPersonProperties = {} as AnalyticsPersonProperties): void {
        if (!userId) return;

        this.identify(userId, {
            user_id: userId,
            ...personProps,
        });

        if (this.sessionStartTracked) return;
        this.sessionStartTracked = true;

        this.trackEvent(AnalyticsEvents.USER_LOGGED_IN, { login_method: loginMethod });

    }

    identify(userId: string, properties: AnalyticsPersonProperties = {} as AnalyticsPersonProperties): void {
        if (!userId) return;

        this.currentUserId = userId;
        if (!this.initialized) return;

        try {
            posthog.identify(userId, {
                user_id: userId,
                ...this.sanitize(properties),
            });

            console.log('[Analytics] identified user:', userId, properties);
        } catch (error) {
            console.warn('[Analytics] identify failed', error);
        }
    }

    reset(): void {
        this.currentUserId = null;
        this.sessionId = uuidv4();
        this.sessionStartTracked = false;
        if (!this.initialized) return;

        try {
            posthog.reset();
        } catch (error) {
            console.warn('[Analytics] reset failed', error);
        }
    }

    private sanitize(payload: AnalyticsEventProperties): AnalyticsEventProperties {
        const out: AnalyticsEventProperties = {};

        Object.keys(payload || {}).forEach((key) => {
            if ((ANALYTICS_BLOCKED_KEYS as readonly string[]).includes(key)) return;
          if (payload[key] !== undefined) out[key] = payload[key];
        });

        return out;
    }
}